import { forwardRef, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface EquipmentLookupItem {
  unit: string;
  license: string;
  model: string;
  condition: string;
  triaxle?: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  // Equipment list to search/display when the dropdown is open. Order is preserved;
  // results are capped at 50 rows visible to keep the dropdown lightweight.
  equipment: ReadonlyArray<EquipmentLookupItem>;
  // True: focus opens a search dropdown filtered by typed value (unit/license/model
  // substring). False: plain text input, no dropdown. Drives the GWI-only / VAN-only
  // gating in callers.
  searchEnabled: boolean;
  // Intercept for dropdown row selection. When provided, replaces the default
  // `onChange(item.unit)` so callers can run extra logic before applying the unit —
  // e.g., the chassis triaxle-mismatch confirm dialog. The caller is responsible
  // for ultimately calling onChange if they want the selection to land.
  onSelectItem?: (item: EquipmentLookupItem) => void;
  // Show the Triaxle column in the dropdown. Defaults to false (vans).
  showTriaxleColumn?: boolean;
  onBlur?: (value: string) => void;
  inputClassName?: string;
  // ClassName applied to the wrapping div. Useful when the lookup sits inside a
  // flex row and needs to take the remaining width (e.g., `flex-1 min-w-0`).
  wrapperClassName?: string;
  dropdownWidth?: number;
  readOnly?: boolean;
  placeholder?: string;
  ariaLabel?: string;
}

// Shared "type + search results table" lookup used for both chassis and van
// equipment selection on the order surfaces. Mirrors the rich dropdown that
// originated in the Order Details modal's chassis input. Free-form text input
// is always allowed — the dropdown only suggests, never constrains.
//
// The dropdown is rendered through createPortal to document.body with fixed
// positioning so an ancestor's `overflow: hidden` (e.g., the OrderFormView
// grid wrapper) can't clip it. Position tracks the input via getBoundingClientRect
// and refreshes on scroll/resize while open.
const EquipmentLookup = forwardRef<HTMLInputElement, Props>(function EquipmentLookup(
  {
    value,
    onChange,
    equipment,
    searchEnabled,
    onSelectItem,
    showTriaxleColumn = false,
    onBlur,
    inputClassName,
    wrapperClassName,
    dropdownWidth = 400,
    readOnly,
    placeholder,
    ariaLabel,
  },
  ref,
) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const setInputRef = (el: HTMLInputElement | null) => {
    inputRef.current = el;
    if (typeof ref === 'function') {
      ref(el);
    } else if (ref) {
      ref.current = el;
    }
  };

  // Compute the portaled dropdown's viewport position from the input. Called on
  // open and on scroll/resize so the dropdown stays anchored under the input.
  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    const update = () => {
      const el = inputRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPosition({ top: rect.bottom + 4, left: rect.left });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen]);

  const filtered = searchEnabled && searchTerm
    ? equipment.filter((e) => {
        const q = searchTerm.toLowerCase();
        return (
          e.unit.toLowerCase().includes(q) ||
          e.license.toLowerCase().includes(q) ||
          e.model.toLowerCase().includes(q)
        );
      })
    : equipment;

  const handleSelect = (item: EquipmentLookupItem) => {
    if (onSelectItem) {
      onSelectItem(item);
    } else {
      onChange(item.unit);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div ref={wrapperRef} className={`relative ${wrapperClassName ?? ''}`}>
      <input
        ref={setInputRef}
        type="text"
        readOnly={readOnly}
        aria-label={ariaLabel}
        placeholder={placeholder}
        className={inputClassName}
        value={isOpen ? searchTerm : value}
        onChange={(e) => {
          const next = e.target.value;
          if (searchEnabled && isOpen) {
            setSearchTerm(next);
          }
          onChange(next);
        }}
        onFocus={() => {
          if (!searchEnabled || readOnly) return;
          setSearchTerm(value);
          setIsOpen(true);
        }}
        onBlur={(e) => {
          // Delay close so a mousedown on a dropdown row can register first.
          // Without this, the row click is swallowed by blur tearing down the
          // dropdown before onClick fires. Also accept focus that landed inside
          // the portaled dropdown (defensive — row clicks use mouseDown +
          // preventDefault so focus stays on the input in practice).
          const blurValue = e.target.value;
          setTimeout(() => {
            const active = document.activeElement;
            const stillInside =
              wrapperRef.current?.contains(active) === true ||
              dropdownRef.current?.contains(active) === true;
            if (!stillInside) {
              setIsOpen(false);
              setSearchTerm('');
            }
          }, 200);
          onBlur?.(blurValue);
        }}
      />
      {searchEnabled && isOpen && position && createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[9999] max-h-60 overflow-auto bg-panel border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl"
          style={{ top: position.top, left: position.left, width: dropdownWidth }}
        >
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900/80">
              <tr>
                <th className="text-left px-2 py-1 font-medium text-muted">Unit</th>
                <th className="text-left px-2 py-1 font-medium text-muted">License</th>
                <th className="text-left px-2 py-1 font-medium text-muted">Model</th>
                <th className="text-left px-2 py-1 font-medium text-muted">Condition</th>
                {showTriaxleColumn && (
                  <th className="text-left px-2 py-1 font-medium text-muted">Tri</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 50).map((eq) => (
                <tr
                  key={eq.unit}
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(eq); }}
                  className="cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                >
                  <td className="px-2 py-1 text-indigo-600 dark:text-indigo-400 font-medium">{eq.unit}</td>
                  <td className="px-2 py-1 text-label">{eq.license || '-'}</td>
                  <td className="px-2 py-1 text-label">{eq.model || '-'}</td>
                  <td className="px-2 py-1 text-label">{eq.condition || '-'}</td>
                  {showTriaxleColumn && (
                    <td className="px-2 py-1 text-label">{eq.triaxle ?? '-'}</td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={showTriaxleColumn ? 5 : 4}
                    className="px-2 py-3 text-center text-muted"
                  >
                    No matching equipment
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>,
        document.body,
      )}
    </div>
  );
});

export default EquipmentLookup;